import { zodResolver } from '@hookform/resolvers/zod';
import {
  type ApiClient,
  ApiError,
  CatalogueAvailabilityController_list_v1,
  type CatalogueItemResource,
  CatalogueItemsController_restore_v1,
  CatalogueItemsController_retire_v1,
  CatalogueItemsController_update_v1,
  type CatalogueItemsController_update_v1Body,
  type OutletResource,
  type OwnerCatalogueItemResource,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import { Label } from '@repo/ui/components/label';
import { Switch } from '@repo/ui/components/switch';
import { IconAlertTriangle, IconBuildingStore } from '@tabler/icons-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { z } from 'zod';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';
import { messageForFailure } from '@/api/failure';
import { barcodeOnTheWire, catalogueNameSchema, optionalBarcodeSchema } from '@/lib/field-schemas';
import { IDEMPOTENCY_KEY_HEADER, newIdempotencyKey } from '@/lib/idempotency-key';
import { formatPaise } from '@/lib/money';

import { fieldLabel, fieldSurface, FormDialogContent } from '../auth/AuthFrame';
import { catalogueAvailabilityPath } from '../paths';
import { barcodeRefusal, barcodeSubject } from './barcode-refusal';
import { BarcodeField } from './BarcodeField';
import { isRetired, labelForKind, mayTrackStock } from './catalogue-kinds';

/**
 * One catalogue item, opened from the list: `PATCH /v1/catalogue/items/{id}`,
 * `DELETE /v1/catalogue/items/{id}` and `POST …/restore`.
 *
 * ## The form has three fields, and price is not the fourth
 *
 * `UpdateCatalogueItemDto` carries `name`, `tracksStock` and `barcode` and
 * nothing else, so
 * `kind` and `code` are absent for the reason `EditOutletDialog` records: they
 * are immutable, and a disabled field reads as a broken one.
 *
 * **A price field is absent for a different reason, and it is worth separating
 * the two.** A price is not an attribute of an item that this route declines to
 * edit — it is a *row*, appended, with the old one staying readable as history
 * (R-202, R-203), which is why a sale can still answer "which price was that"
 * after the price has moved. Rendering it here as a disabled input would teach
 * exactly the wrong model: that there is one price and this screen will not let
 * you touch it. The price is shown beside the code and the kind as what it is —
 * the amount in force, and the row a sale would reference — and changing it is
 * an append that happens elsewhere.
 *
 * ## Turning stock tracking on can be refused, and the refusal names a place
 *
 * An item may be sellable at an outlet that carries no stock while it counts
 * nothing; turning counting on would make the pair FR-208 forbids, so the API
 * refuses with `CATALOGUE_STOCK_NOT_AVAILABLE_HERE` (409) and writes nothing.
 * This is the third of four doors into that state and the one that is easiest to
 * walk through by accident — create a product at the gate with counting off,
 * then turn it on here.
 *
 * **The offending outlet is named from a read this dialog makes, not from the
 * refusal.** The ticket says the server puts it in `details`; it does not —
 * `assertStockIsCarried` selects the offending row and then throws
 * `new AppException('CATALOGUE_STOCK_NOT_AVAILABLE_HERE')` with no second
 * argument, so nothing about which outlet comes back. `NewCatalogueItemDialog`
 * already records the same gap for the create. Reported again in the PR body.
 * What this can do is ask `GET /v1/catalogue/items/{id}/outlets` — which answers
 * `OutletResource`, `carriesStock` included — and say the name out loud, because
 * "one of the outlets" is not a sentence anybody can act on. If that read fails
 * or disagrees, the message falls back to the rule, which is what
 * `error-messages.ts` supplies.
 *
 * ## Retiring says what it does not do
 *
 * Retirement is a soft delete, and the two things a person guesses wrong about
 * it are both stated in the confirmation rather than discovered afterwards:
 *
 * - **The code is not released.** This is the opposite of an outlet code, whose
 *   own confirmation promises it comes back. A catalogue code is carried by two
 *   *total* unique indexes on `(kind, code)` because it lands on receipts and in
 *   money history, so creating a new item with it still answers
 *   `CATALOGUE_CODE_TAKEN`. "Delete" reads as "free the name", and here it does
 *   not.
 * - **Tills stop offering it within one poll, not instantly.** Retiring moves
 *   the outlet's catalogue version — two of the four events that move it are
 *   this and its restore — and a terminal notices on its next staleness check.
 *   Somebody who retires an item and walks to the gate expecting it gone needs
 *   to know which of those two they are watching.
 *
 * ## Restoring is not creating, and it sets no price
 *
 * `POST …/restore` brings the same row back with its history intact and appends
 * nothing: the item resumes at the price it was retired at (R-211). A dialog
 * that offered a price field here would be describing a route that does not
 * exist, so the retired view states the amount it will come back at and offers
 * one button.
 *
 * The confirmation is a stage of this dialog rather than a second one on top of
 * it, for the reason `EditOutletDialog` gives: two stacked dialogs put a focus
 * trap inside a focus trap for a question about the thing already open.
 */

/**
 * What this dialog will send.
 *
 * Three fields, because `UpdateCatalogueItemDto` has three. The name rule is
 * imported rather than restated — the create form judges it by the same one, and
 * two copies of a 120-character ceiling are two copies that eventually disagree.
 */
const editCatalogueItemSchema = z.object({
  name: catalogueNameSchema,
  tracksStock: z.boolean(),
  barcode: optionalBarcodeSchema,
});

type EditCatalogueItemValues = z.infer<typeof editCatalogueItemSchema>;

/** Which question the dialog is asking. One value, so two cannot overlap. */
type Stage = 'editing' | 'confirming-retire';

export function EditCatalogueItemDialog({
  item,
  outlets,
  onOpenChange,
  onUpdated,
  onRetired,
  onRestored,
}: {
  item: CatalogueItemResource;
  /** The park's outlets, to name the one a barcode refusal is about. */
  outlets: readonly OutletResource[];
  onOpenChange: (open: boolean) => void;
  onUpdated: (item: OwnerCatalogueItemResource) => void;
  onRetired: (item: CatalogueItemResource) => void;
  onRestored: (item: CatalogueItemResource) => void;
}) {
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <FormDialogContent>
        {isRetired(item) ? (
          <RetiredItem item={item} outlets={outlets} onRestored={onRestored} />
        ) : (
          // Mounted per open, like the create form, so a half-typed rename and
          // the refusal about it do not wait for the next person to open it —
          // and so the idempotency key below belongs to one attempt at changing
          // one item.
          <EditableItem item={item} outlets={outlets} onUpdated={onUpdated} onRetired={onRetired} />
        )}
      </FormDialogContent>
    </Dialog>
  );
}

/**
 * What identifies the item, and what it currently sells for.
 *
 * The first two do not change: `kind` is immutable (R-201) and `code` is unique
 * for all time within its kind (FR-215). The third does change, but never here —
 * see the note at the top of this file for why it is a fact rather than a field.
 *
 * Values in the faces they carry everywhere else: the code is read aloud and
 * typed back in, and the amount is compared down a column at cash-up, so both
 * keep the mono face and tabular figures they have in the table.
 */
function Identity({ item }: { item: CatalogueItemResource }) {
  return (
    // The one contrast trap in this system: `text-muted-foreground` on
    // `bg-muted` measures 4.39:1 and misses AA. The values here are
    // `text-foreground` for that reason; the labels take the signage class,
    // which is already `muted-foreground` and is used at label size elsewhere.
    <div className="flex flex-wrap gap-x-10 gap-y-4 rounded-2xl bg-muted px-4 py-3">
      <div className="space-y-1.5">
        <p className={fieldLabel}>Code</p>
        <p className="font-mono text-foreground numeric">{item.code}</p>
      </div>
      <div className="space-y-1.5">
        <p className={fieldLabel}>Kind</p>
        <p className="text-foreground">{labelForKind(item.kind)}</p>
      </div>
      <div className="space-y-1.5">
        <p className={fieldLabel}>Selling price</p>
        <p className="font-mono text-foreground numeric">{formatPaise(item.sellingPaise)}</p>
      </div>
    </div>
  );
}

function EditableItem({
  item,
  outlets,
  onUpdated,
  onRetired,
}: {
  item: CatalogueItemResource;
  outlets: readonly OutletResource[];
  onUpdated: (item: OwnerCatalogueItemResource) => void;
  onRetired: (item: CatalogueItemResource) => void;
}) {
  const client = useApiClient();

  const form = useForm({
    resolver: zodResolver(editCatalogueItemSchema),
    mode: 'onTouched',
    defaultValues: { name: item.name, tracksStock: item.tracksStock, barcode: item.barcode ?? '' },
  });

  const [stage, setStage] = useState<Stage>('editing');
  const [formError, setFormError] = useState<string | null>(null);

  // One key for as long as this dialog is open. It unmounts on success, so the
  // next attempt gets a fresh one; a refusal rolls the claim back server-side,
  // so a corrected resubmit re-claims cleanly. The same key is safe across the
  // edit and the retire below — the API's record is unique on `(scope, key)`
  // and each of those writes is its own scope.
  const [idempotencyKey] = useState(newIdempotencyKey);

  const [retiring, setRetiring] = useState(false);

  const pending = form.formState.isSubmitting || retiring;

  // `isDirty` rather than a hand-written comparison: the defaults above are the
  // item as it stands, so react-hook-form is already answering exactly this
  // question and a second answer could disagree with it.
  const changed = form.formState.isDirty;

  const handleSubmit = async ({ name, tracksStock, barcode }: EditCatalogueItemValues) => {
    if (pending) return;

    setFormError(null);

    try {
      // The shape comes from the generated client, and it is the shape that
      // makes the rest of the item immutable: there is no `kind`, no `code` and
      // no price on `UpdateCatalogueItemDto`, so this file could not send one if
      // it wanted to.
      //
      // Both fields go every time rather than only the dirty ones. The API
      // refuses a body that names neither, which the disabled submit already
      // prevents, and restating a value that did not move costs nothing: the
      // stock check fires only on a genuine off-to-on transition, so an
      // unchanged `tracksStock: true` cannot earn a refusal.
      //
      // The barcode is the exception, and goes only when it was changed: absent
      // leaves it as it is, and `null` clears it (#319).
      const body: CatalogueItemsController_update_v1Body = {
        name,
        tracksStock,
        ...(form.formState.dirtyFields.barcode === true
          ? { barcode: barcodeOnTheWire(barcode) }
          : {}),
      };

      onUpdated(
        await client.request(CatalogueItemsController_update_v1, {
          path: { id: item.id },
          body,
          headers: { [IDEMPOTENCY_KEY_HEADER]: idempotencyKey },
        }),
      );
    } catch (error) {
      if (error instanceof ApiError && error.code === 'CATALOGUE_STOCK_NOT_AVAILABLE_HERE') {
        // Against the switch, because the switch is what was refused — and the
        // focus goes with it, so somebody who submitted with the keyboard is not
        // left with the caret on the button and a message to go and find.
        form.setError('tracksStock', { message: await stockRefusal(client, item.id, error) });
        form.setFocus('tracksStock');
      } else if (error instanceof ApiError && error.code === 'CATALOGUE_BARCODE_TAKEN') {
        form.setError('barcode', { message: barcodeRefusal(error, outlets) });
        form.setFocus('barcode');
      } else {
        // Everything else is about the form rather than about one field — a
        // lapsed session, a role that does not permit it, an item somebody else
        // retired while this was open — so it goes above the submit, where it
        // stays until it is dealt with.
        setFormError(messageForFailure(error));
      }

      // Nothing is cleared. The retry is one corrected field.
    }
  };

  const handleRetire = async () => {
    if (pending) return;

    setFormError(null);
    setRetiring(true);

    try {
      onRetired(
        await client.request(CatalogueItemsController_retire_v1, {
          path: { id: item.id },
          headers: { [IDEMPOTENCY_KEY_HEADER]: idempotencyKey },
        }),
      );
    } catch (error) {
      setFormError(messageForFailure(error));

      // Back to the form, because the confirmation's copy is about a thing that
      // did not happen and leaving it up alongside a refusal reads as though it
      // half did.
      setStage('editing');
    } finally {
      setRetiring(false);
    }
  };

  if (stage === 'confirming-retire') {
    return (
      <>
        <DialogHeader>
          <DialogTitle>Retire {item.name}?</DialogTitle>
          <DialogDescription>Retiring is not deleting.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm text-foreground">
          <p>
            Its price history stays readable and every sale that referenced it still reconciles.
            Nothing that happened is lost, and it can be restored later at the price it is retired
            at.
          </p>

          {/* The line this confirmation exists for. An outlet's code comes back
              when the outlet is retired, and somebody who has retired an outlet
              will reasonably expect the same here. It is the opposite. */}
          <p>
            Its code <span className="font-mono text-foreground numeric">{item.code}</span>{' '}
            <span className="font-medium">is not released.</span> Catalogue codes are never
            reissued, so creating a new item with this one is still refused — a replacement needs a
            different code.
          </p>

          <p>
            It leaves the catalogue list unless retired ones are shown, and the tills that sell it
            stop offering it on their next check for new prices rather than the moment you press
            this — a poll away, not instant.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" disabled={pending} onClick={() => setStage('editing')}>
            Keep it on sale
          </Button>
          <Button variant="destructive" disabled={pending} onClick={() => void handleRetire()}>
            {pending ? 'Retiring…' : 'Yes, retire it'}
          </Button>
        </DialogFooter>
      </>
    );
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>{item.name}</DialogTitle>
        <DialogDescription>
          What this item is called, its barcode, and whether its stock is counted.
        </DialogDescription>
      </DialogHeader>

      {/* One column, per docs/ui-conventions.md. `noValidate` because the
          browser's own bubbles are not this app's error language. */}
      <Form {...form}>
        <form
          className="space-y-5"
          noValidate
          onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
        >
          <Identity item={item} />

          <p className="text-sm text-muted-foreground">
            A code and a kind cannot be changed once the item exists — a mistake in either is
            retired and created again under a different code. The price is not edited either: a new
            price is appended and the old one stays readable, which is how a sale from last month
            still knows what it charged.
          </p>

          {/* The way to the third thing an item has, after its name and its
              price: the outlets it may be sold at (FE023). A link and not a
              field, because that set is replaced as a whole by a route of its
              own — a picker inside this form would be a second submit inside one
              `<form>`, and its Save would mean something different from this
              one's. */}
          <AvailabilityLink item={item} />

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel required className={fieldLabel}>
                  Name
                </FormLabel>
                <FormControl
                  render={
                    <Input
                      className={`${fieldSurface} h-11`}
                      required
                      placeholder="Cola 300ml"
                      autoComplete="off"
                      {...field}
                    />
                  }
                />
                <FormDescription>What a person reads on a button and on a receipt.</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <BarcodeField />

          <FormField
            control={form.control}
            name="tracksStock"
            render={({ field }) => (
              <FormItem>
                <StockSwitch
                  item={item}
                  checked={field.value}
                  onCheckedChange={(checked) => field.onChange(checked)}
                  // What makes `setFocus('tracksStock')` in the refusal handler
                  // move anything. Without it react-hook-form holds a bare
                  // `{ name }` for this field, `focus` is undefined, and the
                  // call is a silent no-op — the failure mode where the code
                  // reads as though it does something and does not.
                  switchRef={field.ref}
                />
                {/* The 409 lands here. `FormMessage` renders nothing while
                    there is nothing to say, so the panel above keeps its shape
                    until something is refused. */}
                <FormMessage />
              </FormItem>
            )}
          />

          {formError !== null && (
            <Alert variant="destructive">
              <IconAlertTriangle />
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}

          <DialogFooter className="sm:justify-between">
            {/* Not the screen's accent and not a row control: the way out of an
                item's life sits at the far end of its own form, where it is
                found deliberately rather than brushed past. */}
            <Button
              type="button"
              variant="outline"
              className="text-destructive"
              disabled={pending}
              onClick={() => setStage('confirming-retire')}
            >
              Retire item
            </Button>

            {/* Disabled until something differs. Every mutating request writes
                an audit entry, and a PATCH that changes nothing is a row in the
                park's history saying somebody opened a dialog. */}
            <Button type="submit" disabled={pending || !changed}>
              {pending ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </>
  );
}

/**
 * The way to the outlets this item may be sold at (FE023).
 *
 * A link out of the dialog rather than anything inside it. The availability set
 * is replaced whole by its own route, so it needs its own Save and its own copy
 * explaining that unticking withdraws — none of which belongs on a form whose
 * Save means "rename this item".
 *
 * It leaves this dialog, which is the reason it sits above the fields rather than
 * beside the submit: somebody who has typed a new name and then follows a link
 * loses the typing, and a link at the far end of a form is a link found by
 * somebody who has already started.
 */
function AvailabilityLink({ item }: { item: CatalogueItemResource }) {
  return (
    <Link
      to={catalogueAvailabilityPath(item.id)}
      className="inline-flex items-center gap-1.5 rounded-3xl text-sm text-muted-foreground underline-offset-4 outline-none hover:text-foreground hover:underline focus-visible:ring-3 focus-visible:ring-ring/30"
    >
      <IconBuildingStore className="size-4" aria-hidden />
      Where {item.name} sells
    </Link>
  );
}

/**
 * The stock switch, and the ids that let a refusal reach a screen reader.
 *
 * `useFormField` rather than `FormControl`: Base UI's switch is a `button` with
 * `role="switch"` and a hidden input beside it, so the `id` `FormControl` would
 * put on it is not the id a `<label for>` binds to. The three ids are taken from
 * the hook directly and wired by hand, which is the same wiring `FormControl`
 * does — the refusal is announced as well as rendered, and the standing hint is
 * still announced alongside it rather than being replaced by it.
 *
 * The switch is disabled for anything that is not a product, matching the create
 * form: only a `PRODUCT` may count stock (FR-212), the API answers
 * `CATALOGUE_STOCK_ON_NON_PRODUCT` to anything else, and
 * `docs/ui-conventions.md` asks that a control the caller cannot use be
 * unavailable rather than shown and refused. There is nothing to force back to
 * `false` here, unlike the create form: `kind` cannot change under it.
 */
function StockSwitch({
  item,
  checked,
  onCheckedChange,
  switchRef,
}: {
  item: CatalogueItemResource;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;

  /**
   * react-hook-form's ref for this field, forwarded to the switch itself.
   *
   * Named rather than taken as `ref`: this is a plain function component and the
   * thing being pointed at is one specific element inside it, so a `ref` prop
   * would be a promise about the component that this is not making.
   */
  switchRef: (instance: unknown) => void;
}) {
  const { error, formDescriptionId, formMessageId } = useFormField();

  const allowed = mayTrackStock(item.kind);

  return (
    // A quiet inset panel, and the one place the contrast trap lives:
    // `text-muted-foreground` on `bg-muted` measures 4.39:1 and misses AA. The
    // text here is `text-foreground` for that reason and no other.
    <div className="flex items-start gap-3 rounded-2xl bg-muted px-4 py-3">
      <Switch
        ref={switchRef}
        id="tracks-stock"
        aria-labelledby="tracks-stock-label"
        aria-describedby={[formDescriptionId, error === undefined ? null : formMessageId]
          .filter(Boolean)
          .join(' ')}
        aria-invalid={error !== undefined}
        className="mt-0.5"
        checked={checked}
        disabled={!allowed}
        onCheckedChange={onCheckedChange}
      />
      <div className="space-y-1">
        <Label
          id="tracks-stock-label"
          htmlFor="tracks-stock"
          className={`text-sm text-foreground ${allowed ? 'cursor-pointer' : ''}`}
        >
          Counts stock
        </Label>
        <FormDescription className="text-foreground/70">
          {allowed
            ? 'On for goods counted on a shelf, off for anything made to order. Turning it on is refused if this item is already sold where stock is not carried.'
            : `Only a product can count stock, so this is unavailable for ${labelForKind(item.kind).toLowerCase()} items.`}
        </FormDescription>
      </div>
    </div>
  );
}

/**
 * A retired item, open, read-only, and restorable.
 *
 * Everything it said while it was on sale, still legible — the whole reason the
 * row is reachable is that somebody is looking something up — and one control,
 * which is the only write the API accepts against it.
 */
function RetiredItem({
  item,
  outlets,
  onRestored,
}: {
  item: CatalogueItemResource;
  outlets: readonly OutletResource[];
  onRestored: (item: CatalogueItemResource) => void;
}) {
  const client = useApiClient();

  // One key per mount, as everywhere else. This view unmounts on success.
  const [idempotencyKey] = useState(newIdempotencyKey);

  const [restoring, setRestoring] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const handleRestore = async () => {
    if (restoring) return;

    setFailure(null);
    setRestoring(true);

    try {
      onRestored(
        await client.request(CatalogueItemsController_restore_v1, {
          path: { id: item.id },
          headers: { [IDEMPOTENCY_KEY_HEADER]: idempotencyKey },
        }),
      );
    } catch (error) {
      // Its barcode was taken while it was retired. Not a field here, so the
      // number goes in the sentence.
      setFailure(
        error instanceof ApiError && error.code === 'CATALOGUE_BARCODE_TAKEN'
          ? barcodeRefusal(error, outlets, barcodeSubject(item))
          : messageForFailure(error),
      );
    } finally {
      setRestoring(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex flex-wrap items-center gap-3">
          {item.name}
          {/* `outline` and not `destructive`, the same as in the list. The end
              of an item's life is an ordinary event, and red is kept for a
              system that refused something or is about to. */}
          <Badge variant="outline">Retired</Badge>
        </DialogTitle>
        <DialogDescription>
          Kept for history. Nothing here can be changed while it is retired.
        </DialogDescription>
      </DialogHeader>

      <Identity item={item} />

      <div className="space-y-1.5">
        <p className={fieldLabel}>Stock</p>
        <p className="text-sm text-foreground">{item.tracksStock ? 'Counted' : 'Not counted'}</p>
      </div>

      <div className="space-y-1.5">
        <p className={fieldLabel}>Barcode</p>
        <p className="font-mono text-sm text-foreground numeric">{item.barcode ?? 'None'}</p>
      </div>

      <div className="space-y-4 text-sm text-foreground">
        <p>
          Its price history is unaffected by retirement and by restoring, and its code stays spent —
          no other item can be created with it.
        </p>

        {/* Readable while it is retired, and not changeable: the API refuses
            every write against a retired item, availability included. Somebody
            here is looking something up, and where it used to sell is part of
            that. */}
        <AvailabilityLink item={item} />

        {/* Said before the button rather than discovered after it. Somebody who
            expects to set a price while restoring is expecting a route that
            does not exist: this appends nothing and the item resumes at the
            amount above. */}
        <p>
          Restoring brings this same item back at{' '}
          <span className="font-mono text-foreground numeric">
            {formatPaise(item.sellingPaise)}
          </span>
          , the price it was retired at. No new price is recorded. The tills that sell it offer it
          again on their next check for new prices rather than the moment you press this.
        </p>
      </div>

      {failure !== null && (
        <Alert variant="destructive">
          <IconAlertTriangle />
          <AlertDescription>{failure}</AlertDescription>
        </Alert>
      )}

      <DialogFooter>
        <Button disabled={restoring} onClick={() => void handleRestore()}>
          {restoring ? 'Restoring…' : 'Put it back on sale'}
        </Button>
      </DialogFooter>
    </>
  );
}

/**
 * What to say when the API refuses to turn stock tracking on, and where the
 * outlet's name comes from.
 *
 * The refusal carries no `details` — see the note at the top of this file — so
 * this asks which outlets the item is available at and looks for the ones that
 * carry no stock. Read whole rather than paged: an item's availability is
 * bounded by the park, which has a dozen outlets, and this needs all of them to
 * find the offending one.
 *
 * **Every failure here falls back to the rule rather than throwing.** This runs
 * inside a `catch` that is already handling a refusal; a second failure must not
 * replace the first with a network error about a request the person never made.
 * The same is true when the read comes back and every outlet carries stock,
 * which is a real possibility rather than an impossible one: an outlet's
 * `carriesStock` can be turned off between the write and this read.
 */
async function stockRefusal(client: ApiClient, itemId: string, refusal: ApiError): Promise<string> {
  const rule = messageForFailure(refusal);

  try {
    const outlets = await collectPages<OutletResource>((offset, limit) =>
      client.request(CatalogueAvailabilityController_list_v1, {
        path: { id: itemId },
        query: { offset, limit },
      }),
    );

    const without = outlets.filter((outlet) => !outlet.carriesStock);

    if (without.length === 0) return rule;

    const named = new Intl.ListFormat('en-IN', { type: 'conjunction' }).format(
      without.map((outlet) => outlet.name),
    );

    const one = without.length === 1;

    return `This item is on sale at ${named}, which ${one ? 'does' : 'do'} not carry stock, so it cannot start counting stock while it sells there. Nothing was changed. Take it off ${one ? 'that outlet' : 'those outlets'} first, or leave counting off.`;
  } catch {
    return rule;
  }
}
